import { useCallback, useEffect, useState } from 'react';
import { api, errorText } from '../api';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';

interface Mitarbeiter {
  id: string;
  name: string;
  kuerzel: string | null;
  active: boolean;
  /** Gesetzt, wenn der Kassierer ein Systembenutzer ist — dann hier nur lesbar. */
  user_id: string | null;
}

interface FormState {
  id: string | null;
  name: string;
  kuerzel: string;
}

/**
 * Kassenpersonal — die eigene Kassiererliste.
 *
 * Bewusst getrennt von den Systembenutzern: Wer am Tresen kassiert, braucht
 * keinen WaWi-Login. Wer aber beides ist, wird nicht hier, sondern über das
 * Häkchen "auch Kassierer" in der Benutzerverwaltung geführt — solche Einträge
 * erscheinen hier mit dem Merkmal „Benutzer" und sind schreibgeschützt.
 */
export function StaffPage() {
  const [liste, setListe] = useState<Mitarbeiter[] | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    setError('');
    try {
      setListe(await api<Mitarbeiter[]>('/api/pos/mitarbeiter'));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function speichern() {
    if (!form || !form.name.trim()) return;
    setBusy(true);
    setError('');
    try {
      const body = { name: form.name.trim(), kuerzel: form.kuerzel.trim() || null };
      if (form.id) {
        // Aktiv-Zustand des bestehenden Eintrags beibehalten.
        const vorhanden = liste?.find((m) => m.id === form.id);
        await api(`/api/pos/mitarbeiter/${form.id}`, {
          method: 'PUT',
          body: { ...body, active: vorhanden?.active ?? true },
        });
      } else {
        await api('/api/pos/mitarbeiter', { method: 'POST', body });
      }
      setMessage(`Kassierer ${form.name.trim()} gespeichert.`);
      setForm(null);
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function umschalten(m: Mitarbeiter) {
    setBusy(true);
    setError('');
    try {
      await api(`/api/pos/mitarbeiter/${m.id}`, {
        method: 'PUT',
        body: { name: m.name, kuerzel: m.kuerzel, active: !m.active },
      });
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {form && (
        <Card title={form.id ? 'Kassierer bearbeiten' : 'Neuer Kassierer'}>
          <div className="form-grid">
            <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <TextField
              label="Kürzel"
              value={form.kuerzel}
              onChange={(v) => setForm({ ...form, kuerzel: v })}
              hint="optional, erscheint auf dem Bon"
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="primary" onClick={speichern} disabled={busy || !form.name.trim()}>
              {busy ? 'Speichern …' : 'Speichern'}
            </button>
            <button onClick={() => setForm(null)}>Abbrechen</button>
          </div>
        </Card>
      )}

      <div className="toolbar">
        <span className="small muted">
          Wer an der Kasse steht — eine eigene Liste, nicht dieselbe wie die Systembenutzer. Vor
          jedem Verkauf wird ausgewählt, wer kassiert.
        </span>
        <span className="spacer" />
        <button className="primary" onClick={() => setForm({ id: null, name: '', kuerzel: '' })}>
          Neuer Kassierer
        </button>
      </div>

      <Card padded={false}>
        {!liste ? (
          <Loading />
        ) : liste.length === 0 ? (
          <Empty>Noch niemand angelegt.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Kürzel</th>
                <th>Herkunft</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {liste.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td className="small muted">{m.kuerzel ?? '—'}</td>
                  <td>
                    {m.user_id ? (
                      <Badge kind="info">Benutzer</Badge>
                    ) : (
                      <span className="small muted">eigener Eintrag</span>
                    )}
                  </td>
                  <td>
                    {m.active ? (
                      <Badge kind="ok">aktiv</Badge>
                    ) : (
                      <Badge kind="bad">inaktiv</Badge>
                    )}
                  </td>
                  <td className="num">
                    {m.user_id ? (
                      <span className="small muted" title="Wird über die Benutzerverwaltung gepflegt">
                        über Benutzer
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: 8 }}>
                        <button
                          className="small ghost"
                          onClick={() => setForm({ id: m.id, name: m.name, kuerzel: m.kuerzel ?? '' })}
                        >
                          bearbeiten
                        </button>
                        <button className="small ghost" onClick={() => umschalten(m)} disabled={busy}>
                          {m.active ? 'deaktivieren' : 'aktivieren'}
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
